/**
 * Affiliate Reports Page
 * Personal performance reports and analytics
 */

import { useState, useEffect } from 'react';
import { affiliateApi } from '../../api/affiliate';
import { Line } from 'react-chartjs-2';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';

export default function AffiliateReports() {
  const [performance, setPerformance] = useState<any>(null);
  const [referralsTimeline, setReferralsTimeline] = useState<any[]>([]);
  const [commissionsTimeline, setCommissionsTimeline] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timelineMonths, setTimelineMonths] = useState(12);

  useEffect(() => {
    loadReports();
  }, [dateFrom, dateTo, timelineMonths]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const [perfResponse, refTimelineResponse, commTimelineResponse] = await Promise.all([
        affiliateApi.getPerformanceReport(params.toString()),
        affiliateApi.getReferralsTimeline(timelineMonths),
        affiliateApi.getCommissionsTimeline(timelineMonths),
      ]);

      setPerformance(perfResponse.performance);
      setReferralsTimeline(refTimelineResponse.timeline);
      setCommissionsTimeline(commTimelineResponse.timeline);
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8787/api/v1';

      const response = await fetch(`${baseURL}/affiliate/reports/export/performance?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error} onRetry={loadReports} />
      </div>
    );
  }

  // Chart data for referrals timeline
  const referralsChartData = {
    labels: referralsTimeline.map((item) => {
      const [year, month] = item.month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }),
    datasets: [
      {
        label: 'Referrals',
        data: referralsTimeline.map((item) => item.referrals),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Conversions',
        data: referralsTimeline.map((item) => item.conversions),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Chart data for commissions timeline
  const commissionsChartData = {
    labels: commissionsTimeline.map((item) => {
      const [year, month] = item.month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }),
    datasets: [
      {
        label: 'Total Earnings',
        data: commissionsTimeline.map((item) => item.earnings),
        borderColor: 'rgb(251, 191, 36)',
        backgroundColor: 'rgba(251, 191, 36, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: '#9CA3AF' },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#4B5563',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(75, 85, 99, 0.2)' },
        ticks: { color: '#9CA3AF' },
      },
      y: {
        grid: { color: 'rgba(75, 85, 99, 0.2)' },
        ticks: { color: '#9CA3AF' },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Performance Reports</h1>
          <p className="text-slate-400 mt-1">View your detailed performance analytics</p>
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Export CSV</span>
        </button>
      </div>

      {/* Date Filters */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-slate-700/50 text-white rounded-lg px-3 py-2 border border-slate-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-slate-700/50 text-white rounded-lg px-3 py-2 border border-slate-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
            />
          </div>
          <div className="flex-1" />
          <button
            onClick={() => {
              setDateFrom('');
              setDateTo('');
            }}
            className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition mt-6"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      {performance && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
          <h2 className="text-xl font-semibold text-white mb-6">Performance Metrics</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-400">{performance.totalClicks}</p>
              <p className="text-sm text-slate-400 mt-1">Total Clicks</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-400">{performance.totalReferrals}</p>
              <p className="text-sm text-slate-400 mt-1">Total Referrals</p>
              <p className="text-xs text-gray-500 mt-1">{performance.clickToReferralRate}% from clicks</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">{performance.totalConversions}</p>
              <p className="text-sm text-slate-400 mt-1">Total Conversions</p>
              <p className="text-xs text-gray-500 mt-1">{performance.conversionRate}% rate</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-400">${performance.totalEarnings.toFixed(2)}</p>
              <p className="text-sm text-slate-400 mt-1">Total Earnings</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">${performance.pendingEarnings.toFixed(2)}</p>
              <p className="text-sm text-slate-400 mt-1">Pending Earnings</p>
            </div>
            <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-400">${performance.approvedEarnings.toFixed(2)}</p>
              <p className="text-sm text-slate-400 mt-1">Approved Earnings</p>
            </div>
            <div className="bg-orange-900/20 border border-orange-700 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-400">${performance.paidEarnings.toFixed(2)}</p>
              <p className="text-sm text-slate-400 mt-1">Paid Earnings</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-700/50/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">${performance.averageCommission.toFixed(2)}</p>
              <p className="text-sm text-slate-400 mt-1">Average Commission</p>
            </div>
            <div className="bg-slate-700/50/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{performance.commissionCount}</p>
              <p className="text-sm text-slate-400 mt-1">Total Commissions</p>
            </div>
          </div>
        </div>
      )}

      {/* Referrals Timeline Chart */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Referrals Timeline</h2>
          <select
            value={timelineMonths}
            onChange={(e) => setTimelineMonths(parseInt(e.target.value))}
            className="bg-slate-700/50 text-white rounded-lg px-3 py-2 border border-slate-600"
          >
            <option value={6}>Last 6 Months</option>
            <option value={12}>Last 12 Months</option>
            <option value={24}>Last 24 Months</option>
          </select>
        </div>
        <div style={{ height: '300px' }}>
          <Line data={referralsChartData} options={chartOptions} />
        </div>
      </div>

      {/* Commissions Timeline Chart */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <h2 className="text-xl font-semibold text-white mb-6">Earnings Timeline</h2>
        <div style={{ height: '300px' }}>
          <Line data={commissionsChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}
